import DownloadIcon from '@mui/icons-material/Download';
import LaptopIcon from '@mui/icons-material/Laptop';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import PhonelinkIcon from '@mui/icons-material/Phonelink';
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridFilterModel,
  GridSortModel,
  GridToolbarContainer,
  GridToolbarFilterButton,
} from '@mui/x-data-grid';
import {
  DataGridPremium,
  GridCellParams,
  GridColumnHeaderParams,
  GridRenderCellParams,
  GridRowClassNameParams,
  GridRowId,
  GridRowModesModel,
  GridRowParams,
  GridToolbarColumnsButton,
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import { DateRange } from '@mui/x-date-pickers-pro/models';
import dayjs, { Dayjs } from 'dayjs';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useDebounce } from 'use-debounce';
import {
  useExportAggregatedReportsMutation,
  useFetchReportsWithinDateRangeQuery,
  useUpdateBidPercentagesMutation,
  useUpdateBidsMutation,
} from '../../slices/bidManagementSlice';
import { useFetchFiltersQuery } from '../../slices/filterSlice';
import {
  formatCurrency,
  formatNumber,
  formatPercentage,
} from '../../utils/formatNumbers';
import { getDaysBetweenDates } from '../../utils/getDateRange';
import getNumberFilterOperators from '../../utils/getNumberFilterOperators';
import toCapitalCase from '../../utils/toCapitalCase';
import AdGroupModal from '../AdGroupModal';
import {
  getExcludesFilterOperator,
  getExclusiveFilterOperator,
  getInclusiveFilterOperator,
} from '../MultiselectInputValue';
import './bidManagement.css';
import BulkAdjustBidsByPercentageInput from './BulkAdjustBidByPercentage';
import BulkAdjustBidPercentagesInput from './BulkAdjustBidPercentages';
import BulkNewBidInput from './BulkNewBidInput';
import DateRangeFilter from './DateRangeFilter';
import InlineBidPercentageInput from './InlineBidPercentageInput';
import InlineNewBidInput from './InlineNewBidInput';
import ReportLoadingDialog from './ReportLoadingDialog';
import { DeviceType } from './types';

declare module '@mui/x-data-grid' {
  interface FilterPanelPropsOverrides {
    onKeyDown: (event: KeyboardEvent) => void;
    anchorEl: HTMLElement | null;
    placement: string;
  }
}

interface FilterToolbarProps {
  selectedIds: GridRowId[];
  bidAdjustmentMode: string;
  deviceType: DeviceType;
  handleDeviceTypeChange: (deviceType: DeviceType) => void;
  dateRange: DateRange<Dayjs>;
  handleDateRangeChange: (newDateRange: DateRange<Dayjs>) => void;
  adGroupBids: Record<string, { ad_group_bid: number }>;
  showCompare: boolean;
  handleShowCompare: () => void;
  compareDateRange: DateRange<Dayjs>;
  handleCompareDateRangeChange: (newDateRange: DateRange<Dayjs>) => void;
  includeAccuracyReportClicks: boolean;
  includeAccuracyReportImpressions: boolean;
  combineMatchTypes: boolean;
  setIncludeAccuracyReportClicks: (value: boolean) => void;
  setIncludeAccuracyReportImpressions: (value: boolean) => void;
  setCombineMatchTypes: (value: boolean) => void;
  updateBids: (bidUpdates: any) => any;
  filterModel?: GridFilterModel;
  sortBy?: string;
  sortByDirection?: 'ASC' | 'DESC';
  reportType: string;
}

interface BidManagementDataGridColumn {
  name: string;
  type: string;
  filterable?: boolean;
  width?: number;
  header?: string;
}

interface AdGroupBids {
  [key: string]: { ad_group_bid: number };
}

const renderCurrencyCell = (params: GridRenderCellParams) =>
  params.value == null || params.value === '-'
    ? '-'
    : formatCurrency(params.value as number);

const renderPercentageCell = (params: GridRenderCellParams) =>
  params.value == null || params.value === '-'
    ? '-'
    : formatPercentage(params.value as number);

const renderNumberCell = (params: GridRenderCellParams) =>
  params.value == null || params.value === '-'
    ? '-'
    : formatNumber(params.value as number);

const renderBidInput = (
  params: GridRenderCellParams,
  updateBidsMultiPlatform: (bidUpdates: any) => any
) => {
  if (isTotalRow(params)) {
    return null;
  }
  return (
    <InlineNewBidInput params={params} updateBids={updateBidsMultiPlatform} />
  );
};

const renderAdGroupBid = (
  params: GridRenderCellParams,
  adGroupBids: AdGroupBids
) => {
  if (isTotalRow(params)) {
    return null;
  }

  return (
    formatCurrency(adGroupBids[params.row.ad_group_id]?.ad_group_bid) || 'N/A'
  );
};

const renderCampaignNameCell = (params: GridRenderCellParams) => {
  if (isTotalRow(params)) {
    return null;
  }
  return params.row.campaign_name;
};

const renderCampaignTypeCell = (params: GridRenderCellParams) => {
  if (isTotalRow(params)) {
    return null;
  }
  return params.row.campaign_type;
};

const isTotalRow = (
  params: GridRowClassNameParams | GridCellParams | GridRowParams
) => {
  if (!params.row) {
    return false;
  }
  return params.row.ad_group_id === 'TOTAL_ROW';
};

const renderAdGroupNameCell = (
  params: GridRenderCellParams,
  handleOpen: (adGroupId: string) => void
) => {
  return params.row.is_ad_vantage_managed ? (
    <Link
      component="button"
      onClick={() => handleOpen(params.row.unique_id.split('-')[0])}
    >
      {params.value}
    </Link>
  ) : (
    <Typography sx={{ fontSize: '0.85rem' }}>{params.value}</Typography>
  );
};

const FilterToolbar: React.FC<FilterToolbarProps> = ({
  selectedIds,
  bidAdjustmentMode,
  handleDeviceTypeChange,
  deviceType,
  dateRange,
  handleDateRangeChange,
  adGroupBids,
  showCompare,
  handleShowCompare,
  compareDateRange,
  handleCompareDateRangeChange,
  includeAccuracyReportClicks,
  includeAccuracyReportImpressions,
  combineMatchTypes,
  setIncludeAccuracyReportClicks,
  setIncludeAccuracyReportImpressions,
  setCombineMatchTypes,
  updateBids,
  filterModel,
  sortBy,
  sortByDirection,
  reportType,
}) => {
  const [daysInInitialRange, setDaysInInitialRange] = useState(0);
  const [calendarOneOpen, setCalendarOneOpen] = useState(false);
  const [calendarTwoOpen, setCalendarTwoOpen] = useState(false);

  const [updateBidsPercentages] = useUpdateBidPercentagesMutation();
  const [exportReport, { isLoading: isExporting }] =
    useExportAggregatedReportsMutation();

  useEffect(() => {
    if (dateRange) {
      setDaysInInitialRange(getDaysBetweenDates(dateRange[0], dateRange[1]));
    }
  }, [dateRange]);

  const getValuesFromUniqueId = (uniqueId: string) => {
    let adGroupId = uniqueId.split('-')[0];
    let adGroupIdForBid = adGroupId;

    const isAdVantageManaged = uniqueId.includes('ADVANTAGE');
    const advertisingNetwork = uniqueId.includes('GOOGLE')
      ? 'google-ads'
      : 'microsoft-ads';

    if (isAdVantageManaged) {
      adGroupIdForBid = uniqueId.split('-')[1];
    }

    return {
      adGroupId,
      adGroupIdForBid,
      isAdVantageManaged,
      advertisingNetwork,
    };
  };

  const handleUpdateBidsByPercent = async (percent: number) => {
    const bidUpdates = [];
    for (const id of selectedIds) {
      const {
        adGroupId,
        adGroupIdForBid,
        isAdVantageManaged,
        advertisingNetwork,
      } = getValuesFromUniqueId(id as string);

      bidUpdates.push({
        ad_group_id: adGroupId,
        bid:
          adGroupBids[adGroupIdForBid].ad_group_bid +
          adGroupBids[adGroupIdForBid].ad_group_bid * (percent / 100),
        is_ad_vantage_managed: isAdVantageManaged,
        advertising_network: advertisingNetwork,
      });
    }

    try {
      await updateBids({ bidUpdates }).unwrap();
      enqueueSnackbar('Bids updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error updating bids', { variant: 'error' });
    }
  };

  const handleUpdateBids = async (newBid: string) => {
    const bidUpdates = [];
    for (const id of selectedIds) {
      const { adGroupId, isAdVantageManaged, advertisingNetwork } =
        getValuesFromUniqueId(id as string);

      bidUpdates.push({
        ad_group_id: adGroupId,
        bid: parseFloat(newBid),
        is_ad_vantage_managed: isAdVantageManaged,
        advertising_network: advertisingNetwork,
      });
    }

    try {
      await updateBids({ bidUpdates }).unwrap();
      enqueueSnackbar('Bids updated successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar('Error updating bids', { variant: 'error' });
    }
  };

  const handleUpdateBidPercentages = async (percentage: number) => {
    const bidPercentages = [];
    for (const id of selectedIds) {
      let matchType;
      if (String(id).includes('BROAD')) {
        matchType = 'BROAD';
      } else if (String(id).includes('PHRASE')) {
        matchType = 'PHRASE';
      }
      if (!matchType) {
        throw new Error(
          'Bid percentage adjustments are only applicable to broad / phrase match ad groups.'
        );
      }
      bidPercentages.push({
        ad_group_id: String(id).replace(/-BROAD|-PHRASE/g, ''),
        match_type: matchType,
        bid_percentage: percentage,
      });
    }

    try {
      await updateBidsPercentages({ bidPercentages }).unwrap();
      enqueueSnackbar('Bid percentages updated successfully', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Error updating bid percentages', { variant: 'error' });
    }
  };

  const handleChange = (
    event: React.MouseEvent<HTMLElement>,
    deviceType: DeviceType
  ) => {
    if (deviceType !== null) {
      handleDeviceTypeChange(deviceType);
    }
  };

  const handleExport = async () => {
    try {
      const [startDate, endDate] = dateRange;
      const [compareStartDate, compareEndDate] = compareDateRange;

      const blob = await exportReport({
        view_type: deviceType,
        start_date: startDate ? startDate.format('YYYY-MM-DD') : '',
        end_date: endDate ? endDate.format('YYYY-MM-DD') : '',
        compare_start_date:
          showCompare && compareStartDate
            ? compareStartDate.format('YYYY-MM-DD')
            : '',
        compare_end_date:
          showCompare && compareEndDate
            ? compareEndDate.format('YYYY-MM-DD')
            : '',
        filterModel: filterModel || undefined,
        sortBy,
        sortByDirection,
        includeAccuracyReport:
          includeAccuracyReportClicks || includeAccuracyReportImpressions,
        reportType,
        combineMatchTypes,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ads_report_${reportType}_${startDate?.format(
        'YYYY-MM-DD'
      )}_${endDate?.format('YYYY-MM-DD')}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      enqueueSnackbar('Failed to export reports', { variant: 'error' });
    }
  };

  return (
    <GridToolbarContainer>
      <Box width="100%" display="flex" alignItems="center">
        <GridToolbarFilterButton />
        <GridToolbarColumnsButton />
        <ToggleButtonGroup
          value={deviceType}
          exclusive
          onChange={handleChange}
          aria-label="Device Type"
          color="primary"
        >
          <ToggleButton value="combined" aria-label="Combined">
            <PhonelinkIcon />
          </ToggleButton>
          <ToggleButton value="mobile" aria-label="Mobile">
            <PhoneAndroidIcon />
          </ToggleButton>
          <ToggleButton value="desktop" aria-label="Desktop">
            <LaptopIcon />
          </ToggleButton>
          <ToggleButton value="both" aria-label="Mobile + Desktop">
            <PhoneAndroidIcon />+<LaptopIcon />
          </ToggleButton>
        </ToggleButtonGroup>
        <DateRangeFilter
          dateRange={dateRange}
          onChange={handleDateRangeChange}
          calendarOpen={calendarOneOpen}
          setCalendarOpen={(open) => {
            setCalendarOneOpen(open);
            setCalendarTwoOpen(false);
          }}
        />
        <FormControlLabel
          slotProps={{ typography: { fontSize: '12px', lineHeight: 1 } }}
          labelPlacement="bottom"
          control={
            <Checkbox
              onChange={handleShowCompare}
              size="small"
              checked={showCompare}
              name="showCompare"
              sx={{ padding: '5px' }}
            />
          }
          label="Compare"
        />
        <DateRangeFilter
          initialDateRange={dateRange}
          daysInInitialRange={daysInInitialRange}
          dateRange={compareDateRange}
          onChange={handleCompareDateRangeChange}
          disabled={!showCompare}
          calendarOpen={calendarTwoOpen}
          setCalendarOpen={(open) => {
            setCalendarTwoOpen(open);
            setCalendarOneOpen(false);
          }}
        />
        <Button
          color="primary"
          startIcon={<DownloadIcon />}
          onClick={handleExport}
          size="small"
          sx={{ mx: 4, width: '120px' }}
          variant="contained"
          disabled={isExporting}
        >
          Export
          {isExporting && <CircularProgress size={14} sx={{ ml: 1 }} />}
        </Button>
      </Box>
      <Box width="100%" display="flex" alignItems="center">
        <Typography variant="body1" mr={2} ml={1.5} sx={{ fontWeight: 'bold' }}>
          Match Type Settings:
        </Typography>
        <Typography variant="body1" mr={2} ml={1.5}>
          {' '}
          Combine Match Types
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={combineMatchTypes}
              onChange={(e) => setCombineMatchTypes(e.target.checked)}
              name="combineMatchTypes"
            />
          }
          label=""
        />
        <Typography variant="body1" mr={2} ml={1.5} sx={{ fontWeight: 'bold' }}>
          Accuracy Report:
        </Typography>
        <Typography variant="body1" mr={2} ml={1.5}>
          {' '}
          Include Accuracy Report Clicks:
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeAccuracyReportClicks}
              onChange={(e) => setIncludeAccuracyReportClicks(e.target.checked)}
              name="includeAccuracyReportClicks"
            />
          }
          label=""
        />
        <Typography variant="body1" mr={2} ml={1.5}>
          {' '}
          Include Accuracy Report Impressions:
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeAccuracyReportImpressions}
              onChange={(e) =>
                setIncludeAccuracyReportImpressions(e.target.checked)
              }
              name="includeAccuracyReportImpressions"
            />
          }
          label=""
        />
      </Box>
      <Box width="100%" display="flex" alignItems="center">
        {selectedIds.length > 0 && bidAdjustmentMode === 'BID' && (
          <>
            <Typography
              variant="body1"
              mr={2}
              ml={1.5}
              sx={{ fontWeight: 'bold' }}
            >
              {' '}
              Bulk Update Bids:
            </Typography>
            <BulkAdjustBidsByPercentageInput
              handleClick={handleUpdateBidsByPercent}
            />
            <BulkNewBidInput handleClick={handleUpdateBids} />
          </>
        )}
        {selectedIds.length > 0 && bidAdjustmentMode === 'PERCENTAGE' && (
          <>
            <Typography
              variant="body1"
              mr={2}
              ml={1.5}
              sx={{ fontWeight: 'bold' }}
            >
              {' '}
              Set Bid Percentages:
            </Typography>
            <BulkAdjustBidPercentagesInput
              handleClick={handleUpdateBidPercentages}
              count={selectedIds.length}
            />
          </>
        )}
      </Box>
    </GridToolbarContainer>
  );
};

interface BidManagementDataGridProps {
  reportType: string;
}

const BidManagementDataGrid: React.FC<BidManagementDataGridProps> = ({
  reportType,
}) => {
  const [showCompare, setShowCompare] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>('combined');
  const [selectedIds, setSelectedIds] = useState<GridRowId[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [open, setOpen] = useState(false);
  const [modalAdgroupId, setModelAdGroupId] = useState<string>('');
  const apiRef = useGridApiRef();
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 24,
  });
  const [queryOptions, setQueryOptions] = useState<{
    filterModel?: GridFilterModel;
  }>();
  const [debouncedQueryOptions] = useDebounce(queryOptions, 500);
  const [sortOptions, setSortOptions] = useState<{
    sortBy?: string;
    sortByDirection?: 'ASC' | 'DESC';
  }>();
  const [bidAdjustmentMode, setBidAdjustmentMode] = useState('');

  const [updateBidsMultiPlatform] = useUpdateBidsMutation();

  const onFilterChange = React.useCallback((filterModel: GridFilterModel) => {
    const itemsWithValues = filterModel.items.filter(
      (item) =>
        item.value !== undefined &&
        item.value !== '' &&
        !(Array.isArray(item.value) && item.value.length === 0)
    );
    if (itemsWithValues.length === 0) {
      setQueryOptions(undefined);
      return;
    }
    setQueryOptions({
      filterModel: {
        ...filterModel,
        items: itemsWithValues,
      },
    });
  }, []);

  const onSortChange = React.useCallback((sortModel: GridSortModel) => {
    if (sortModel.length === 0) {
      setSortOptions(undefined);
      return;
    }
    const sort = {
      sortBy: sortModel[0].field as string,
      sortByDirection: sortModel[0].sort?.toUpperCase() as 'ASC' | 'DESC',
    };
    setSortOptions(sort);
  }, []);
  const yesterday = dayjs().subtract(1, 'day');
  const thirtyDaysAgo = yesterday.subtract(30, 'day');
  const sixtyDaysAgo = yesterday.subtract(60, 'day');
  const thirtyOneDaysAgo = yesterday.subtract(31, 'day');
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    thirtyDaysAgo,
    yesterday,
  ]);
  const [compareDateRange, setCompareDateRange] = useState<DateRange<Dayjs>>([
    sixtyDaysAgo,
    thirtyOneDaysAgo,
  ]);
  const [startDate, endDate] = dateRange;
  const [compareStartDate, compareEndDate] = compareDateRange;
  const [includeAccuracyReportClicks, setIncludeAccuracyReportClicks] =
    useState(false);
  const [
    includeAccuracyReportImpressions,
    setIncludeAccuracyReportImpressions,
  ] = useState(false);
  const [combineMatchTypes, setCombineMatchTypes] = useState(true);

  const {
    data = {
      total: 0,
      results: [],
      compare_results: [],
      ad_group_bids: {},
      totals: {},
      compare_totals: {},
      dih_earliest_date: undefined,
    },
    isFetching,
  } = useFetchReportsWithinDateRangeQuery(
    {
      start_date: startDate ? startDate.format('YYYY-MM-DD') : '',
      end_date: endDate ? endDate.format('YYYY-MM-DD') : '',
      compare_start_date:
        showCompare && compareStartDate
          ? compareStartDate.format('YYYY-MM-DD')
          : '',
      compare_end_date:
        showCompare && compareEndDate
          ? compareEndDate.format('YYYY-MM-DD')
          : '',
      ...paginationModel,
      ...debouncedQueryOptions,
      ...sortOptions,
      includeAccuracyReport:
        includeAccuracyReportClicks || includeAccuracyReportImpressions,
      reportType,
      combineMatchTypes,
      deviceType,
    },
    {
      skip: startDate === null || endDate === null,
    }
  );

  const dihDateWarning = (() => {
    if (!data.dih_earliest_date || !startDate) return false;
    return startDate.isBefore(dayjs(data.dih_earliest_date));
  })();

  const getCompareData = (compareResult: any) => {
    if (!compareResult) return {};

    const result: { [key: string]: number } = {};
    const columns = [
      'cost',
      'clicks',
      'impressions',
      'avg_click_through_rate',
      'conversions',
      'conversion_value',
      'conversion_value_cost',
      'avg_cost_per_click',
      'lifetime_estimated_agp',
      'new_projects_created',
      'new_quotable_leads',
      'transaction_leads',
      'call_leads',
      'form_leads',
      'chat_leads',
      'text_message_leads',
      'event_leads',
      'custom_event_leads',
    ];
    const accuracy_columns = [
      'top_word',
      'negative_word',
      'contain_one_word_group_from_ad_group',
      'contain_two_word_groups_from_ad_group',
      'contain_three_word_groups_from_ad_group',
      'contain_four_word_groups_from_ad_group',
      'contain_five_word_groups_from_ad_group',
      'attribute_air_handler_type',
      'attribute_configuration',
      'attribute_diy',
      'attribute_efficiency',
      'attribute_energy_source',
      'attribute_measure',
      'attribute_other',
      'attribute_size',
      'attribute_zone',
      'brand',
      'buying_intent',
      'competitor',
      'concept',
      'core_brand',
      'general',
      'location',
      'model',
      'negative',
      'no_word_group',
      'obsolete_or_related',
      'service_installation',
      'top_of_funnel',
    ];
    if (includeAccuracyReportClicks || includeAccuracyReportImpressions) {
      accuracy_columns.forEach((column) => {
        if (includeAccuracyReportClicks) {
          columns.push(`clicks_${column}`);
        }
        if (includeAccuracyReportImpressions) {
          columns.push(`impressions_${column}`);
        }
      });
    }

    columns.forEach((column) => {
      result[`compare_${column}_total`] = compareResult[`${column}_total`] || 0;
      result['compare_' + column + '_mobile'] =
        compareResult[`${column}_mobile`] || 0;
      result['compare_' + column + '_computer'] =
        compareResult[`${column}_computer`] || 0;
    });

    const dihColumns = [
      'dih_clusters',
      'dih_high_intent_clusters',
      'dih_rate_pct',
      'dih_total_actions',
    ];
    dihColumns.forEach((column) => {
      result[`compare_${column}`] = compareResult[column] ?? null;
    });

    return result;
  };

  const getRows = () => {
    return [...getBaseRows(), ...getTotalRow()];
  };

  const getBaseRows = () => {
    return createDataGridRows(data.results, data.compare_results);
  };

  const getTotalRow = () => {
    if (!data.totals) return [];
    if (Object.keys(data.totals).length === 0) return [];
    return createDataGridRows([data.totals], [data.compare_totals]);
  };

  const createDataGridRows = (results: any, compareResults: any) => {
    if (showCompare) {
      return results.map((result: any, index: any) => {
        const compare = compareResults.length ? compareResults[index] : null;
        return {
          ...result,
          ...getCompareData(compare),
        };
      });
    }
    return results;
  };

  const {
    data: filters = {
      word_combination_filters: [],
      word_group_category_filters: [],
    },
  } = useFetchFiltersQuery({});

  const handleDateRangeChange = (newDateRange: DateRange<Dayjs>) => {
    setDateRange(newDateRange);
  };

  const handleCompareDateRangeChange = (newDateRange: DateRange<Dayjs>) => {
    setCompareDateRange(newDateRange);
  };

  const handleShowCompare = () => {
    setShowCompare((prev) => !prev);
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleDeviceTypeChange = (deviceType: DeviceType) => {
    setDeviceType(deviceType);
  };

  const handleOpen = (adGroupId: string) => {
    setModelAdGroupId(adGroupId);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderHeader = (params: GridColumnHeaderParams) => {
    return (
      <Box
        sx={{
          whiteSpace: 'normal',
          lineHeight: '1.2',
          height: 97,
        }}
      >
        {params?.colDef?.headerName}
      </Box>
    );
  };

  const buildCombinedColumn = (
    field = '',
    headerName = '',
    type = '',
    filterable = false,
    sortable = false,
    minWidth = 100
  ) => {
    let columnType;
    let renderCell: (params: GridRenderCellParams) => string | number;
    switch (type) {
      case 'currency':
        columnType = 'currency';
        renderCell = renderCurrencyCell;
        break;
      case 'percentage':
        columnType = 'percentage';
        renderCell = renderPercentageCell;
        break;
      case 'number':
      default:
        columnType = 'number';
        renderCell = renderNumberCell;
        break;
    }

    return [
      {
        field,
        minWidth,
        flex: 1,
        headerName,
        columnType,
        filterable,
        filterOperators: getNumberFilterOperators(),
        renderCell: (params: GridRenderCellParams) => renderCell(params),
        renderHeader: renderHeader,
        sortable,
        align: 'center',
        resizable: false,
      },
      ...(showCompare
        ? [
            {
              field: `compare_${field}`,
              minWidth: minWidth + 20,
              flex: 1,
              headerName: `Compare ${headerName}`,
              columnType,
              renderCell: (params: GridRenderCellParams) => renderCell(params),
              renderHeader: renderHeader,
              cellClassName: 'comparison',
              headerClassName: 'comparison',
              filterable: false,
              sortable: false,
              resizable: false,
            },
          ]
        : []),
    ];
  };

  const buildColumnsForLeadReport = (deviceType?: string) => {
    if (reportType === 'microsoft-ads' && !combineMatchTypes) {
      return [];
    }

    let devicePostFix = '_total';
    let devicePostFixHeader = '';
    if (deviceType === 'mobile') {
      devicePostFix = '_mobile';
      devicePostFixHeader = ' (Mobile)';
    } else if (deviceType === 'desktop') {
      devicePostFix = '_computer';
      devicePostFixHeader = ' (Desktop)';
    }

    const defaultWidth = 90;

    const columns = [
      {
        name: 'lifetime_estimated_agp',
        header: 'Lifetime Estimated AGP',
        type: 'currency',
        width: 110,
      },
      {
        name: 'new_projects_created',
        type: 'number',
      },
      {
        name: 'new_quotable_leads',
        type: 'number',
        header: 'Quotable Leads',
        width: 110,
      },
      {
        name: 'transaction_leads',
        type: 'number',
        width: 110,
      },
      {
        name: 'call_leads',
        type: 'number',
      },
      {
        name: 'form_leads',
        type: 'number',
      },
      {
        name: 'chat_leads',
        type: 'number',
      },
      {
        name: 'text_message_leads',
        type: 'number',
      },
      {
        name: 'event_leads',
        type: 'number',
      },
      {
        name: 'custom_event_leads',
        type: 'number',
      },
    ];

    let finalColumns: any[] = [];

    columns.forEach((column) => {
      finalColumns = finalColumns.concat(
        buildCombinedColumn(
          `${column.name}${devicePostFix}`,
          `${toCapitalCase(column.name)}${devicePostFixHeader}`,
          column.type,
          true,
          true,
          column.width || defaultWidth
        )
      );
    });

    return finalColumns;
  };

  const buildColumnsForDihReport = () => {
    const columns = [
      { name: 'dih_clusters', header: 'Clusters', type: 'number', width: 90 },
      {
        name: 'dih_high_intent_clusters',
        header: 'DIH Clusters',
        type: 'number',
        width: 100,
      },
      {
        name: 'dih_rate_pct',
        header: 'DIH Rate %',
        type: 'percentage',
        width: 100,
      },
      {
        name: 'dih_total_actions',
        header: 'Total Actions',
        type: 'number',
        width: 110,
      },
    ];

    let finalColumns: any[] = [];

    columns.forEach((column) => {
      finalColumns = finalColumns.concat(
        buildCombinedColumn(
          column.name,
          column.header,
          column.type,
          false,
          true,
          column.width
        )
      );
    });

    return finalColumns;
  };

  const buildCombinedColumnsForPerformanceReport = (deviceType?: string) => {
    const defaultWidth = 90;
    let devicePostFix = '_total';
    let devicePostFixHeader = '';
    if (deviceType === 'mobile') {
      devicePostFix = '_mobile';
      devicePostFixHeader = ' (Mobile)';
    } else if (deviceType === 'desktop') {
      devicePostFix = '_computer';
      devicePostFixHeader = ' (Desktop)';
    }

    const columns: BidManagementDataGridColumn[] = [
      {
        name: 'impressions',
        type: 'number',
        width: 110,
      },
      {
        name: 'clicks',
        type: 'number',
      },
      {
        name: 'avg_click_through_rate',
        header: 'CTR',
        type: 'percentage',
      },
      {
        name: 'cost',
        type: 'currency',
      },
      {
        name: 'avg_cost_per_click',
        header: 'CPC',
        type: 'currency',
      },
      {
        name: 'conversions',
        type: 'number',
        width: 110,
      },
      {
        name: 'conversion_value',
        type: 'currency',
        width: 110,
      },
      {
        name: 'conversion_value_cost',
        header: 'Conversion Value / Cost',
        type: 'currency',
        width: 110,
      },
    ];

    let finalColumns: any[] = [];

    columns.forEach((column) => {
      finalColumns = finalColumns.concat(
        buildCombinedColumn(
          `${column.name}${devicePostFix}`,
          `${column.header || toCapitalCase(column.name)}${devicePostFixHeader}`,
          column.type,
          true,
          true,
          column.width || defaultWidth
        )
      );
    });

    return finalColumns;
  };

  const buildCombinedColumnsForAccuracyReport = (
    includeClicks = true,
    includeImpressions = true,
    deviceType?: string
  ) => {
    let devicePostFix = '_total';
    let devicePostFixHeader = '';
    if (deviceType === 'mobile') {
      devicePostFix = '_mobile';
      devicePostFixHeader = ' (Mobile)';
    } else if (deviceType === 'desktop') {
      devicePostFix = '_computer';
      devicePostFixHeader = ' (Desktop)';
    }

    const containsWordFromAdGroupColumns: BidManagementDataGridColumn[] = [
      {
        name: 'impressions_contain_one_word_group_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'clicks_contain_one_word_group_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'impressions_contain_two_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'clicks_contain_two_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'impressions_contain_three_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'clicks_contain_three_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'impressions_contain_four_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'clicks_contain_four_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'impressions_contain_five_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
      {
        name: 'clicks_contain_five_word_groups_from_ad_group',
        type: 'number',
        filterable: false,
        width: 170,
      },
    ];

    let columns: BidManagementDataGridColumn[] = [
      {
        name: 'impressions_top_word',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_top_word',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_negative_word',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_negative_word',
        type: 'number',
        filterable: false,
      },
      ...(reportType === 'google-ads'
        ? [...containsWordFromAdGroupColumns]
        : []),
      {
        name: 'impressions_attribute_air_handler_type',
        type: 'number',
        filterable: false,
        width: 130,
      },
      {
        name: 'clicks_attribute_air_handler_type',
        type: 'number',
        filterable: false,
        width: 130,
      },
      {
        name: 'impressions_attribute_configuration',
        type: 'number',
        filterable: false,
        width: 130,
      },
      {
        name: 'clicks_attribute_configuration',
        type: 'number',
        filterable: false,
        width: 130,
      },
      {
        name: 'impressions_attribute_diy',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_attribute_diy',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_attribute_efficiency',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_attribute_efficiency',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_attribute_energy_source',
        type: 'number',
        filterable: false,
        width: 120,
      },
      {
        name: 'clicks_attribute_energy_source',
        type: 'number',
        filterable: false,
        width: 120,
      },
      {
        name: 'impressions_attribute_measure',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_attribute_measure',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_attribute_other',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_attribute_other',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_attribute_size',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_attribute_size',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_attribute_zone',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_attribute_zone',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_brand',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_brand',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_buying_intent',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_buying_intent',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_competitor',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_competitor',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_concept',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_concept',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_core_brand',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_core_brand',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_general',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_general',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_location',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_location',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_model',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_model',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_negative',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_negative',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_no_word_group',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_no_word_group',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_obsolete_or_related',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_obsolete_or_related',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_service_installation',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_service_installation',
        type: 'number',
        filterable: false,
      },
      {
        name: 'impressions_top_of_funnel',
        type: 'number',
        filterable: false,
      },
      {
        name: 'clicks_top_of_funnel',
        type: 'number',
        filterable: false,
      },
    ];

    columns = columns.filter((column) => {
      return includeClicks || !column.name.includes('clicks');
    });

    columns = columns.filter((column) => {
      return includeImpressions || !column.name.includes('impressions');
    });

    let finalColumns: any[] = [];

    columns.forEach((column) => {
      finalColumns = finalColumns.concat(
        buildCombinedColumn(
          `${column.name}${devicePostFix}`,
          `${toCapitalCase(column.name)}${devicePostFixHeader}`,
          column.type,
          column.filterable,
          false,
          column.width || 110
        )
      );
    });

    return finalColumns;
  };

  const combineDeviceColumns = (columns: any[]) => {
    const length = columns.length;
    const finalOrder = [];
    for (let i = 0; i < length / 2; i++) {
      finalOrder.push(columns[i]);
      finalOrder.push(columns[i + length / 2]);
    }
    return finalOrder;
  };

  const getDeviceTypeColumns = () => {
    const includeAccuracyReport =
      includeAccuracyReportClicks || includeAccuracyReportImpressions;
    const accuracyReportColumnsBothDevices = includeAccuracyReport
      ? combineDeviceColumns([
          ...buildCombinedColumnsForAccuracyReport(
            includeAccuracyReportClicks,
            includeAccuracyReportImpressions,
            'mobile'
          ),
          ...buildCombinedColumnsForAccuracyReport(
            includeAccuracyReportClicks,
            includeAccuracyReportImpressions,
            'desktop'
          ),
        ])
      : [];
    const accuracyReportColumnsMobile = includeAccuracyReport
      ? buildCombinedColumnsForAccuracyReport(
          includeAccuracyReportClicks,
          includeAccuracyReportImpressions,
          'mobile'
        )
      : [];
    const accuracyReportColumnsDesktop = includeAccuracyReport
      ? buildCombinedColumnsForAccuracyReport(
          includeAccuracyReportClicks,
          includeAccuracyReportImpressions,
          'desktop'
        )
      : [];
    const accuracyReportColumns = includeAccuracyReport
      ? buildCombinedColumnsForAccuracyReport(
          includeAccuracyReportClicks,
          includeAccuracyReportImpressions
        )
      : [];

    const dihColumns = buildColumnsForDihReport();

    switch (deviceType) {
      case 'both':
        return [
          ...combineDeviceColumns([
            ...buildCombinedColumnsForPerformanceReport('mobile'),
            ...buildCombinedColumnsForPerformanceReport('desktop'),
          ]),
          ...combineDeviceColumns([
            ...buildColumnsForLeadReport('mobile'),
            ...buildColumnsForLeadReport('desktop'),
          ]),
          ...dihColumns,
          ...accuracyReportColumnsBothDevices,
        ];
      case 'mobile':
        return [
          ...buildCombinedColumnsForPerformanceReport('mobile'),
          ...buildColumnsForLeadReport('mobile'),
          ...dihColumns,
          ...accuracyReportColumnsMobile,
        ];
      case 'desktop':
        return [
          ...buildCombinedColumnsForPerformanceReport('desktop'),
          ...buildColumnsForLeadReport('desktop'),
          ...dihColumns,
          ...accuracyReportColumnsDesktop,
        ];
      case 'combined':
        return [
          ...buildCombinedColumnsForPerformanceReport(),
          ...buildColumnsForLeadReport(),
          ...dihColumns,
          ...accuracyReportColumns,
        ];
      default:
        return [];
    }
  };

  const availableColumnsMap: any = {};
  availableColumnsMap['new_bid'] = {
    field: 'new_bid',
    headerName: 'New Bid',
    width: 125,
    resizable: false,
    renderCell: (params: GridRenderCellParams) =>
      renderBidInput(params, updateBidsMultiPlatform),
    renderHeader: renderHeader,
    sortable: false,
    filterable: false,
  };
  availableColumnsMap['bid_percentage'] = {
    field: 'bid_percentage',
    headerName: 'Bid Percentage',
    width: 125,
    resizable: false,
    renderCell: (params: GridCellParams) => (
      <InlineBidPercentageInput {...params} />
    ),
    renderHeader: renderHeader,
    sortable: false,
    filterable: false,
  };
  availableColumnsMap['ad_group_bid'] = {
    field: 'ad_group_bid',
    width: 85,
    headerName: 'Current Bid',
    renderCell: (params: GridRenderCellParams) =>
      renderAdGroupBid(params, data.ad_group_bids),
    renderHeader: renderHeader,
    sortable: false,
    type: 'number',
    filterable: false,
  };
  availableColumnsMap['adgroup_name'] = {
    field: 'adgroup_name',
    width: 700,
    minWidth: 400,
    maxWidth: 1200,
    resizable: true,
    headerName: 'Ad Group Name',
    filterOperators: [
      ...getInclusiveFilterOperator(
        reportType === 'google-ads' ? filters.word_combination_filters : []
      ),
      ...getExclusiveFilterOperator(
        reportType === 'google-ads' ? filters.word_combination_filters : []
      ),
      ...getExcludesFilterOperator(
        reportType === 'google-ads' ? filters.word_group_category_filters : []
      ),
    ],
    sortable: true,
    filterable: true,
    renderCell: (params: GridRenderCellParams) =>
      renderAdGroupNameCell(params, handleOpen),
    renderHeader: renderHeader,
    hideable: false,
  };
  availableColumnsMap['campaign_name'] = {
    field: 'campaign_name',
    flex: 1,
    maxWidth: 250,
    minWidth: 175,
    resizable: true,
    headerName: 'Campaign Name',
    sortable: true,
    filterable: true,
    renderHeader: renderHeader,
    hideable: false,
    renderCell: renderCampaignNameCell,
  };
  availableColumnsMap['campaign_type'] = {
    field: 'campaign_type',
    flex: 1,
    maxWidth: 130,
    minWidth: 130,
    resizable: true,
    headerName: 'Campaign Type',
    sortable: true,
    filterable: true,
    renderHeader: renderHeader,
    hideable: false,
    renderCell: renderCampaignTypeCell,
  };
  availableColumnsMap['match_type'] = {
    field: 'match_type',
    flex: 1,
    maxWidth: 120,
    minWidth: 120,
    headerName: 'Match Type',
    sortable: true,
    filterable: true,
    renderHeader: renderHeader,
    hideable: false,
    resizable: false,
  };

  const baseColumns = [
    availableColumnsMap['new_bid'],
    availableColumnsMap['ad_group_bid'],
    availableColumnsMap['adgroup_name'],
    availableColumnsMap['campaign_name'],
    availableColumnsMap['campaign_type'],
  ];

  const microsoftAdsReportColumns = [...baseColumns];
  const googleAdsReportColumns = [...baseColumns];

  if (!combineMatchTypes) {
    microsoftAdsReportColumns.push(availableColumnsMap['match_type']);
    googleAdsReportColumns.push(availableColumnsMap['match_type']);
    googleAdsReportColumns.unshift(availableColumnsMap['bid_percentage']);
  }

  const getColumns = () => {
    if (reportType === 'microsoft-ads') {
      return microsoftAdsReportColumns.concat(getDeviceTypeColumns());
    }
    return googleAdsReportColumns.concat(getDeviceTypeColumns());
  };

  return (
    <Card sx={{ marginTop: 3 }}>
      {dihDateWarning && (
        <Alert severity="warning" sx={{ m: 2 }}>
          DIH cluster data is only available from {data.dih_earliest_date}. The
          selected date range extends before this date, so Clusters, DIH
          Clusters, DIH Rate %, and Total Actions columns may not reflect
          accurate values for the full range.
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <div style={{ width: '100%', display: 'flex', height: '85vh' }}>
              <DataGridPremium
                sx={{
                  minWidth: '730px',
                  '.MuiDataGrid-columnHeaders': {
                    minHeight: 97,
                    height: 97,
                  },
                  '.MuiDataGrid-columnHeader': {
                    whiteSpace: 'normal',
                    lineHeight: '1.2',
                    height: '100%',
                    textAlign: 'center',
                    position: 'relative',
                  },
                  '.MuiDataGrid-columnHeaderTitleContainer': {
                    whiteSpace: 'normal',
                    lineHeight: '1.2',
                    height: 'auto',
                    display: 'flex',
                    padding: '0 4px',
                    paddingTop: '30px',
                    paddingBottom: '6px',
                  },
                  '.MuiDataGrid-iconButtonContainer': {
                    position: 'absolute',
                    bottom: '0',
                    left: '40%',
                  },
                  '.MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                  '[data-field="adgroup_name"] .MuiDataGrid-columnSeparator': {
                    display: 'block',
                    visibility: 'visible',
                    '&:hover': {
                      backgroundColor: '#999999',
                    },
                  },
                  '.MuiDataGrid-menuIcon': {
                    position: 'absolute',
                    left: '40%',
                    top: '-10%',
                    opacity: 0,
                    transition: 'opacity 0.2s ease-in-out',
                    transform: 'rotate(90deg)',
                  },
                  '.MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
                    opacity: 1,
                  },
                  '.MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:nth-of-type(odd)':
                    {
                      backgroundColor: '#ffffff',
                    },
                  '.MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:nth-of-type(even)':
                    {
                      backgroundColor: '#f5f5f5',
                    },
                  '.MuiDataGrid-cell:nth-of-type(odd)': {
                    backgroundColor: '#ffffff',
                  },
                  '.MuiDataGrid-cell:nth-of-type(even)': {
                    backgroundColor: '#f5f5f5',
                  },
                  '.total-row': {
                    backgroundColor: '#f0f0f0',
                    fontWeight: 'bold',
                    borderTop: '2px solid #666',
                    '& .MuiDataGrid-cell': {
                      backgroundColor: '#f0f0f0 !important',
                    },
                  },
                  '.MuiDataGrid-virtualScroller': {
                    top: '0px',
                    overflow: 'auto !important',
                    '&::-webkit-scrollbar': {
                      '-webkit-appearance': 'none',
                      width: '12px',
                      height: '12px',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      borderRadius: '4px',
                      border: '2px solid white',
                      backgroundColor: 'rgba(0, 0, 0, .3)',
                    },
                  },
                }}
                columnHeaderHeight={97}
                showCellVerticalBorder
                showColumnVerticalBorder
                checkboxSelection
                disableMultipleRowSelection={true}
                disableRowSelectionOnClick
                disableRowGrouping
                disableAggregation
                disableColumnReorder
                disableVirtualization
                autoHeight={true}
                rows={getRows()}
                columns={getColumns()}
                rowCount={data.total}
                pagination={true}
                paginationMode="server"
                loading={isFetching}
                filterMode="server"
                onFilterModelChange={onFilterChange}
                sortingMode="server"
                onSortModelChange={onSortChange}
                pageSizeOptions={[24, 48, 96]}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                getRowId={(row) => row.unique_id}
                onRowSelectionModelChange={(selections) => {
                  const hasSelections = selections.length > 0;

                  if (!hasSelections) {
                    setBidAdjustmentMode('');
                  } else {
                    const firstSelection = selections[0].toString();
                    const isAdVantageManaged =
                      firstSelection.includes('ADVANTAGE');

                    if (
                      isAdVantageManaged &&
                      (firstSelection.includes('BROAD') ||
                        firstSelection.includes('PHRASE'))
                    ) {
                      setBidAdjustmentMode('PERCENTAGE');
                    } else {
                      setBidAdjustmentMode('BID');
                    }
                  }

                  setSelectedIds(selections);
                }}
                isRowSelectable={(params: GridRowParams) => {
                  if (isTotalRow(params)) {
                    return false;
                  }

                  const percentageBidOnly =
                    params.row.is_ad_vantage_managed &&
                    ['BROAD', 'PHRASE'].includes(params.row.match_type);

                  return (
                    (bidAdjustmentMode === 'PERCENTAGE' && percentageBidOnly) ||
                    bidAdjustmentMode === 'BID' ||
                    bidAdjustmentMode === ''
                  );
                }}
                rowModesModel={rowModesModel}
                onRowModesModelChange={handleRowModesModelChange}
                slots={{ toolbar: FilterToolbar }}
                apiRef={apiRef}
                initialState={{
                  pinnedColumns: {
                    left: [
                      GRID_CHECKBOX_SELECTION_COL_DEF.field,
                      'adgroup_name',
                    ],
                    right: [],
                  },
                }}
                slotProps={{
                  toolbar: {
                    selectedIds,
                    bidAdjustmentMode,
                    handleDeviceTypeChange,
                    deviceType,
                    dateRange,
                    handleDateRangeChange,
                    adGroupBids: data.ad_group_bids,
                    showCompare,
                    handleShowCompare,
                    compareDateRange,
                    handleCompareDateRangeChange,
                    disableColumnGrouping: true,
                    disableAggregation: true,
                    includeAccuracyReportClicks,
                    includeAccuracyReportImpressions,
                    combineMatchTypes,
                    setIncludeAccuracyReportClicks,
                    setIncludeAccuracyReportImpressions,
                    setCombineMatchTypes,
                    updateBids: updateBidsMultiPlatform,
                    filterModel:
                      debouncedQueryOptions?.filterModel || undefined,
                    sortBy: sortOptions?.sortBy,
                    sortByDirection: sortOptions?.sortByDirection,
                    reportType,
                  },
                  filterPanel: {
                    onKeyDown: (event: KeyboardEvent) => {
                      if (
                        event.key === 'Escape' ||
                        (event.key === 'Enter' && !event.shiftKey)
                      ) {
                        apiRef.current.hideFilterPanel();
                      }
                    },
                    sx: {
                      position: 'absolute',
                      top: `-110px !important`,
                      right: '-310px',
                      backgroundColor: '#fff',
                      border: '1px solid #ccc',
                      width: '650px',
                    },
                  },
                }}
                getRowClassName={(params: GridRowClassNameParams) => {
                  if (isTotalRow(params)) {
                    return 'total-row';
                  }
                  return '';
                }}
              />
            </div>
            <AdGroupModal
              open={open}
              handleClose={handleClose}
              adgroup_id={modalAdgroupId}
            />
            <ReportLoadingDialog open={isFetching} onClose={() => {}} />
          </Paper>
        </Grid>
      </Grid>
    </Card>
  );
};

export default BidManagementDataGrid;
